import { findBucketIndex, recordTimestamp } from './dashboardPeriods';
import { collectPlayerUsernames } from './dashboardTree';

export const verticalOf = (platform) => {
  const value = String(platform || '').toLowerCase();
  if (value === 'pm8' || value === 'casino') return 'casino';
  if (value === 'sport' || value === 'sports') return 'sport';
  return null;
};

const toNumber = (value) => {
  const number = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

const isPresent = (value) => value !== undefined && value !== null && value !== '';

const emptyTotals = () => ({ ggr: 0, ngr: 0, turnover: 0, bets: 0, hasNgr: false, hasBets: false });

const emptySummary = () => ({ ...emptyTotals(), casino: emptyTotals(), sport: emptyTotals(), records: 0 });

const addTo = (totals, record, weight) => {
  totals.ggr += toNumber(record.ggr) * weight;
  totals.turnover += toNumber(record.turnover) * weight;
  if (isPresent(record.ngr)) {
    totals.ngr += toNumber(record.ngr) * weight;
    totals.hasNgr = true;
  }
  if (isPresent(record.total_bets)) {
    totals.bets += toNumber(record.total_bets) * weight;
    totals.hasBets = true;
  }
};

const addRecord = (summary, record, weight) => {
  summary.records += 1;
  addTo(summary, record, weight);
  const vertical = verticalOf(record.platform);
  if (vertical) addTo(summary[vertical], record, weight);
};

/** GGR, NGR, turnover and bets in total and per vertical. `weightOf` pro-rates records (see cutoffWeight). */
export const summarizeRecords = (records, weightOf) => {
  const summary = emptySummary();
  (records || []).forEach((record) => {
    const weight = weightOf ? weightOf(record) : 1;
    if (weight > 0) addRecord(summary, record, weight);
  });
  return summary;
};

export const holdRate = (totals) => (totals && totals.turnover > 0 ? (totals.ggr / totals.turnover) * 100 : null);

/** Casino and sport share of GGR; only meaningful while both sides are non-negative. */
export const ggrShares = (summary) => {
  const casino = summary?.casino?.ggr || 0;
  const sport = summary?.sport?.ggr || 0;
  if (casino < 0 || sport < 0 || casino + sport <= 0) return null;
  return { casino: (casino / (casino + sport)) * 100, sport: (sport / (casino + sport)) * 100 };
};

/** Change vs. a baseline; the percentage is only defined for a positive baseline. */
export const computeDelta = (current, previous) => {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  const abs = current - previous;
  return { abs, pct: previous > 0 ? (abs / previous) * 100 : null };
};

/** Per-bucket and cumulative GGR (total, casino, sport) over a range's buckets. */
export const buildSeries = (records, range) => {
  const points = range.buckets.map((bucket) => ({ key: bucket.key, total: 0, casino: 0, sport: 0 }));
  (records || []).forEach((record) => {
    const ms = recordTimestamp(record);
    if (ms === null) return;
    const index = findBucketIndex(range, ms);
    if (index < 0) return;
    const ggr = toNumber(record.ggr);
    points[index].total += ggr;
    const vertical = verticalOf(record.platform);
    if (vertical) points[index][vertical] += ggr;
  });

  let cumTotal = 0;
  let cumCasino = 0;
  let cumSport = 0;
  return points.map((point) => {
    cumTotal += point.total;
    cumCasino += point.casino;
    cumSport += point.sport;
    return { ...point, cumTotal, cumCasino, cumSport };
  });
};

/**
 * The viewer's direct reports, plus a lookup from every player below them to the direct report they
 * belong to. Rollup records carry the direct report as `user_id`, player records the player.
 */
export const buildDirectReportIndex = (tree) => {
  const reports = [];
  const playerToReport = new Map();
  (tree?.children || []).forEach((child) => {
    const players = collectPlayerUsernames(child);
    players.forEach((username) => playerToReport.set(username, child.username));
    reports.push({
      username: child.username,
      name: child.name || child.username,
      type: child.type,
      playerCount: players.length
    });
  });
  return { reports, reportUsernames: new Set(reports.map((report) => report.username)), playerToReport };
};

export const totalsByDirectReport = (records, index, weightOf) => {
  const byReport = new Map();
  (records || []).forEach((record) => {
    const userId = record.user_id ?? record.userId;
    const owner = index.reportUsernames.has(userId) ? userId : index.playerToReport.get(userId);
    if (!owner) return;
    const weight = weightOf ? weightOf(record) : 1;
    if (!(weight > 0)) return;
    if (!byReport.has(owner)) byReport.set(owner, emptySummary());
    addRecord(byReport.get(owner), record, weight);
  });
  return byReport;
};

/** One row per direct report, ranked by GGR, with its casino/sport split, share and change. */
export const buildBreakdownRows = (index, currentByReport, previousByReport) => {
  const rows = index.reports.map((report) => {
    const current = currentByReport.get(report.username);
    const ggr = current?.ggr || 0;
    return {
      ...report,
      ggr,
      casinoGgr: current?.casino.ggr || 0,
      sportGgr: current?.sport.ggr || 0,
      turnover: current?.turnover || 0,
      active: Boolean(current && (current.turnover !== 0 || current.ggr !== 0 || current.bets > 0)),
      delta: previousByReport ? computeDelta(ggr, previousByReport.get(report.username)?.ggr || 0) : null
    };
  });

  const totalGgr = rows.reduce((sum, row) => sum + row.ggr, 0);
  return rows
    .map((row) => ({ ...row, share: totalGgr > 0 ? (row.ggr / totalGgr) * 100 : null }))
    .sort((a, b) => b.ggr - a.ggr);
};

const emptyUserTotals = (username) => ({ username, ggr: 0, casinoGgr: 0, sportGgr: 0, turnover: 0, bets: 0 });

/** Per-user totals from hourly or daily records. */
export const totalsByUser = (records) => {
  const byUser = new Map();
  (records || []).forEach((record) => {
    const userId = record.user_id ?? record.userId;
    if (!userId) return;
    if (!byUser.has(userId)) byUser.set(userId, emptyUserTotals(userId));
    const totals = byUser.get(userId);
    const ggr = toNumber(record.ggr);
    totals.ggr += ggr;
    totals.turnover += toNumber(record.turnover);
    totals.bets += toNumber(record.total_bets);
    const vertical = verticalOf(record.platform);
    if (vertical === 'casino') totals.casinoGgr += ggr;
    else if (vertical === 'sport') totals.sportGgr += ggr;
  });
  return byUser;
};

/** Per-user totals from the analytics API's `aggregate: true` response (an object keyed by user). */
export const totalsFromUserAggregates = (aggregates) => {
  const byUser = new Map();
  Object.values(aggregates || {}).forEach((entry) => {
    if (!entry?.user_id) return;
    const totals = emptyUserTotals(entry.user_id);
    totals.ggr = toNumber(entry.ggr);
    totals.turnover = toNumber(entry.turnover);
    totals.bets = toNumber(entry.total_bets);
    Object.entries(entry.platform_breakdown || {}).forEach(([platform, values]) => {
      const vertical = verticalOf(platform);
      if (vertical === 'casino') totals.casinoGgr += toNumber(values?.ggr);
      else if (vertical === 'sport') totals.sportGgr += toNumber(values?.ggr);
    });
    byUser.set(entry.user_id, totals);
  });
  return byUser;
};

const isActiveUser = (totals) => totals.turnover > 0 || totals.bets > 0 || totals.ggr !== 0;

/**
 * Players who played, the biggest contributors to GGR and the biggest winners (most negative GGR).
 * `playerUsernames` keeps agent rollup rows out when records mix the two.
 */
export const rankPlayers = (userTotals, { playerUsernames, limit = 10 } = {}) => {
  const players = Array.from(userTotals?.values() || [])
    .filter((totals) => !playerUsernames || playerUsernames.has(totals.username))
    .filter(isActiveUser);
  return {
    activeCount: players.length,
    top: players.filter((player) => player.ggr > 0).sort((a, b) => b.ggr - a.ggr).slice(0, limit),
    winners: players.filter((player) => player.ggr < 0).sort((a, b) => a.ggr - b.ggr).slice(0, limit)
  };
};
